import { Button, Form, Tab, Tabs } from "react-bootstrap";

import FeatureFlag from "./FeatureFlag";
import FeatureFlagAssignmentTable from "./FeatureFlagAssignmentTable";
import FormatFeatureFlagDate from "./FormatFeatureFlagDate";

interface FeatureFlagDetailsPanelProps {
  flag: FeatureFlag;
  /** Same handler as the main table so panel and table stay in sync. */
  onToggleEnabled: (id: number, enabled: boolean) => void;
  onDeleteFlag: (id: number) => void;
}

/**
 * Details panel shown next to the feature flag table when a row is selected.
 * Shows the flag's details, audit dates, and the enabled toggle; the Edit
 * button is display-only until flag editing is implemented.
 */
function FeatureFlagDetailsPanel({
  flag,
  onToggleEnabled,
  onDeleteFlag,
}: FeatureFlagDetailsPanelProps) {
  return (
    <aside className="featureFlagDetailsPanel" aria-label={`Details for ${flag.name}`}>
      <h5 className="panelTitle">{flag.name}</h5>

      <div className="detailRow">
        <span className="detailLabel">Details: </span>
        {flag.description}
      </div>
      <div className="detailRow">
        <span className="detailLabel">Start Date: </span>
        {FormatFeatureFlagDate(flag.startDate)}
      </div>
      <div className="detailRow">
        <span className="detailLabel">End Date: </span>
        {FormatFeatureFlagDate(flag.endDate)}
      </div>
      <div className="detailRow enabledRow">
        <span className="detailLabel">Enabled: </span>
        <Form.Check
          type="switch"
          id={`feature-flag-details-enabled-${flag.id}`}
          className="mb-0 enabledSwitch"
          checked={flag.enabled}
          onChange={(e) => onToggleEnabled(flag.id, e.target.checked)}
          aria-label={`Enabled status for ${flag.name}`}
        />
      </div>
      <div className="detailRow">
        <span className="detailLabel">Created Date: </span>
        {FormatFeatureFlagDate(flag.createdDate)}
      </div>
      <div className="detailRow">
        <span className="detailLabel">Last Modified Date: </span>
        {FormatFeatureFlagDate(flag.lastModifiedDate)}
      </div>

      <div className="panelActions">
        <Button variant="primary" size="sm" type="button">
          Edit
        </Button>
        <Button
          variant="primary"
          size="sm"
          type="button"
          onClick={() => onDeleteFlag(flag.id)}
        >
          Delete
        </Button>
      </div>

      {/* Keyed by flag id so tab choice, search, and paging reset when the
          selected flag changes. */}
      <Tabs
        key={flag.id}
        defaultActiveKey="tenants"
        id={`feature-flag-details-tabs-${flag.id}`}
        className="detailsTabs"
      >
        <Tab eventKey="tenants" title="Tenants">
          <FeatureFlagAssignmentTable
            itemLabel="Tenant"
            idPrefix={`flag-${flag.id}-tenants`}
            rows={flag.tenants.map((tenant) => ({
              id: tenant.id,
              name: tenant.tenantName,
              startDate: tenant.startDate,
              endDate: tenant.endDate,
            }))}
          />
        </Tab>
        <Tab eventKey="facilities" title="Facilities">
          <FeatureFlagAssignmentTable
            itemLabel="Facility"
            idPrefix={`flag-${flag.id}-facilities`}
            rows={flag.facilities.map((facility) => ({
              id: facility.id,
              name: facility.facilityName,
              startDate: facility.startDate,
              endDate: facility.endDate,
            }))}
          />
        </Tab>
      </Tabs>
    </aside>
  );
}

export default FeatureFlagDetailsPanel;
